import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import { TimeSeriesData, ModelData, PrevisionData } from '../types/prevision';
import { Article } from '../types/api.types';
import {
  calculateAverageModelQuality,
  resolvePointModelQuality,
} from '../utils/modelQuality';

const mapPredictionItem = (item: any): TimeSeriesData => ({
  date: item.date,
  quantity: item.sales || 0,
  confidence: resolvePointModelQuality(item),
  modelQualityStatus: item.model_quality_status ?? null,
  articlesWithQuality: item.articles_with_quality,
  articlesTotal: item.articles_total,
  has_correction: item.has_correction || false,
  corrected_articles: item.corrected_articles || [],
  breakdown: item.breakdown,
  impacted_articles: item.impacted_articles,
});

export const usePrevisionData = (databaseId: number | string | null) => {
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [articlesHash, setArticlesHash] = useState<string>(''); // Track articles changes
  const [forceRefetch, setForceRefetch] = useState<boolean>(false); // Force refetch when articles change
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const shouldFetchPredictions = useRef<boolean>(false); // Track if predictions should be fetched
  const isFetchingPredictionsRef = useRef<boolean>(false); // Prevent re-entry / infinite loop
  
  const [previsionData, setPrevisionData] = useState<PrevisionData>({
    historicalData: [],
    modelPredictions: [],
    articles: [],
    isLoading: false,
    error: null
  });

  // Fetch available models from API
  const fetchAvailableModels = useCallback(async () => {
    if (!databaseId) return;
    
    try {
      const response = await api.get(
        `${API_ROUTES.PREDICTION.BASE}${API_ROUTES.PREDICTION.AVAILABLE_MODELS(databaseId)}`
      );
      const models = response.data || [];
      setAvailableModels(models);
      
      // Initialize model predictions with status from available models
      // API now returns 'status' field (from computation server) instead of 'model_status'
      setPrevisionData(prev => {
        const existingModelIds = new Set(prev.modelPredictions.map(m => m.id));
        const newModels: ModelData[] = models
          .filter((m: any) => !existingModelIds.has(m.id))
          .map((model: any) => ({
            id: model.id,
            name: model.name,
            data: [],
            isLoading: false,
            error: null,
            status: model.status || 'not_trained',  // Use 'status' from API (computation server)
            hasPredictions: model.has_predictions || false
          }));
        
        // Update existing models with status if they don't have it
        const updatedModels = prev.modelPredictions.map(existingModel => {
          const modelInfo = models.find((m: any) => m.id === existingModel.id);
          if (modelInfo) {
            // Always update status from API (source of truth)
            return {
              ...existingModel,
              status: modelInfo.status || existingModel.status || 'not_trained',
              hasPredictions: modelInfo.has_predictions ?? existingModel.hasPredictions ?? false
            };
          }
          return existingModel;
        });
        
        return {
          ...prev,
          modelPredictions: [...updatedModels, ...newModels]
        };
      });
    } catch (error) {
      console.error('Failed to fetch available models:', error);
      // Fallback to default models if API fails
      setAvailableModels([
        { id: 'sarima', name: 'SARIMA', model_status: 'not_trained', has_predictions: false },
        { id: 'prophet', name: 'Prophet', model_status: 'not_trained', has_predictions: false },
        { id: 'lstm', name: 'LSTM', model_status: 'not_trained', has_predictions: false },
        { id: 'hybrid_s_p', name: 'Hybrid', model_status: 'not_trained', has_predictions: false },
      ]);
    }
  }, [databaseId]);

  // Fetch historical data based on filters
  const fetchHistoricalData = useCallback(async () => {
    if (!databaseId) return;
    
    setPrevisionData(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await api.post(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.AGGREGATED_SALES(databaseId)}`,
        { filters }
      );
      
      // Transform to TimeSeriesData[]
      const historicalData: TimeSeriesData[] = Array.isArray(response.data) 
        ? response.data.map((item: any) => ({
            date: item.date,
            quantity: item.quantity || item.sales || 0,
            confidence: null,
            has_correction: false, // Historical data doesn't have correction
            corrected_articles: [] // Historical data doesn't have corrections
          }))
        : [];
      
      setPrevisionData(prev => ({ 
        ...prev, 
        historicalData,
        isLoading: false 
      }));
    } catch (error) {
      setPrevisionData(prev => ({ 
        ...prev, 
        isLoading: false,
        error: 'Failed to fetch historical data' 
      }));
    }
  }, [databaseId, filters]);

  // Fetch articles based on filters
  const fetchArticles = useCallback(async () => {
    if (!databaseId) return;
    
    try {

      
      const response = await api.post(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.AGGREGATED_SALES(databaseId)}`,
        { filters }
      );
           
      const articles: Article[] = response.data || [];
      
     
      // Create a hash of article IDs to track changes
      const newArticlesHash = articles.map(a => a.id).sort().join(',');
      const articlesChanged = newArticlesHash !== articlesHash;
      
      setPrevisionData(prev => ({ ...prev, articles }));
      setArticlesHash(newArticlesHash);
      
      // After updating articles, fetch the historical data for these articles
      fetchHistoricalData();
      
      // If articles changed, we need to refetch predictions (don't clear - we only refetch models with predictions)
      if (articlesChanged) {
        setForceRefetch(true);
      }
    } catch (error) {
      console.error('usePrevisionData - Error fetching articles:', error);
      setPrevisionData(prev => ({ 
        ...prev,
        error: 'Failed to fetch articles' 
      }));
    }
  }, [databaseId, filters, fetchHistoricalData, articlesHash]);

  // Fetch predictions for a specific model
  const fetchModelPredictions = useCallback(async (modelId: string) => {
    if (!databaseId) return;
    
    // Find or create model in state
    setPrevisionData(prev => {
      const existingModelIndex = prev.modelPredictions.findIndex(m => m.id === modelId);
      
      let updatedModels = [...prev.modelPredictions];
      
      if (existingModelIndex >= 0) {
        // Update existing model's loading state
        const modelInfo = availableModels.find(m => m.id === modelId);
        updatedModels[existingModelIndex] = {
          ...updatedModels[existingModelIndex],
          isLoading: true,
          error: null, // Clear previous error
          status: modelInfo?.status || updatedModels[existingModelIndex].status || 'not_trained',  // Use 'status' from API
          hasPredictions: modelInfo?.has_predictions ?? updatedModels[existingModelIndex].hasPredictions ?? false
        };
      } else {
        // Add new model with loading state
        const modelInfo = availableModels.find(m => m.id === modelId);
        updatedModels.push({
          id: modelId,
          name: modelInfo?.name || modelId,
          data: [],
          isLoading: true,
          error: null,
          status: modelInfo?.status || 'not_trained',  // Use 'status' from API
          hasPredictions: modelInfo?.has_predictions || false
        });
      }
      
      return { ...prev, modelPredictions: updatedModels };
    });
    
    try {
      const response = await api.post(
        `${API_ROUTES.PREDICTION.BASE}${API_ROUTES.PREDICTION.PREDICTION_DATA(databaseId, modelId)}`,
        {
          model_name: modelId,
          filters
        }
      );
      
      // Transform to TimeSeriesData[] using backend metadata
      const timeSeriesData: TimeSeriesData[] = Array.isArray(response.data.predictions) 
        ? response.data.predictions.map(mapPredictionItem)
        : [];
      
      // Update model in state
      setPrevisionData(prev => {
        const existingModelIndex = prev.modelPredictions.findIndex(m => m.id === modelId);
        
        let updatedModels = [...prev.modelPredictions];
        
        if (existingModelIndex >= 0) {
          // Update existing model
          const modelInfo = availableModels.find(m => m.id === modelId);
          updatedModels[existingModelIndex] = {
            ...updatedModels[existingModelIndex],
            data: timeSeriesData,
            isLoading: false,
            error: null,
            status: modelInfo?.status || updatedModels[existingModelIndex].status || 'available',  // Use 'status' from API
            hasPredictions: true // We have predictions now
          };
        } else {
          // Should not happen, but handle it anyway
          const modelInfo = availableModels.find(m => m.id === modelId);
          updatedModels.push({
            id: modelId,
            name: modelInfo?.name || modelId,
            data: timeSeriesData,
            isLoading: false,
            error: null,
            status: modelInfo?.status || 'available',  // Use 'status' from API
            hasPredictions: true
          });
        }
        
        return { ...prev, modelPredictions: updatedModels };
      });
    } catch (error: any) {
      // Determine if this is a real error or just missing predictions
      const modelInfo = availableModels.find(m => m.id === modelId);
      const modelStatus = modelInfo?.status || 'not_trained';  // Use 'status' from API
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch predictions';
      const is404 = error.response?.status === 404;
      const isNoPredictionError = is404 && errorMessage.toLowerCase().includes('no prediction');
      
      // Only treat as error if:
      // 1. Status is 'error' (actual error state)
      // 2. Status is 'available' but we got a non-404 error (unexpected error)
      // 3. Status is not 'not_trained' and we got a non-404 error
      const isRealError = modelStatus === 'error' || 
                         (modelStatus === 'available' && !is404) ||
                         (modelStatus !== 'not_trained' && modelStatus !== 'training' && modelStatus !== 'predicting' && !is404);
      
      setPrevisionData(prev => {
        const existingModelIndex = prev.modelPredictions.findIndex(m => m.id === modelId);
        
        let updatedModels = [...prev.modelPredictions];
        
        if (existingModelIndex >= 0) {
          // Update existing model
          updatedModels[existingModelIndex] = {
            ...updatedModels[existingModelIndex],
            isLoading: false,
            error: isRealError ? errorMessage : null, // Only set error if it's a real error
            status: modelStatus,
            hasPredictions: modelInfo?.has_predictions || false
          };
        } else {
          // Add model
          updatedModels.push({
            id: modelId,
            name: modelInfo?.name || modelId,
            data: [],
            isLoading: false,
            error: isRealError ? errorMessage : null, // Only set error if it's a real error
            status: modelStatus,
            hasPredictions: modelInfo?.has_predictions || false
          });
        }
        
        return { 
          ...prev, 
          modelPredictions: updatedModels
          // Don't set global error for model-specific errors
        };
      });
    }
  }, [databaseId, filters, availableModels]);

  // NOTE: Auto-selection is now handled entirely in Previsions.tsx
  // Removed this effect to avoid conflicts

  // Apply filters and trigger data fetching
  const applyFilters = useCallback((newFilters: Record<string, any>) => {
    setFilters(newFilters);
    shouldFetchPredictions.current = true; // Mark that predictions need to be fetched
  }, []);

  // Fetch historical data and articles when filters change
  useEffect(() => {
    fetchHistoricalData();
    fetchArticles();
  }, [fetchHistoricalData, fetchArticles]);

  // Single effect to handle all prediction fetching logic
  useEffect(() => {
    if (!databaseId || availableModels.length === 0) return;
    
    // Prevent re-entry: avoid infinite loop when state updates trigger effect re-runs
    if (isFetchingPredictionsRef.current) return;
    
    // Check if we have actual prediction data (not just initialized empty models)
    const modelsWithData = previsionData.modelPredictions.filter(m => m.data && m.data.length > 0);
    const hasNoPredictionData = modelsWithData.length === 0;
    
    // Fetch predictions if:
    // 1. Force refetch is true (articles changed)
    // 2. Should fetch predictions flag is set (filters changed)
    // 3. No models have prediction data yet (initial load or after models initialized)
    if (forceRefetch || shouldFetchPredictions.current || hasNoPredictionData) {
      isFetchingPredictionsRef.current = true;
      
      (async () => {
        try {
          if (!databaseId || availableModels.length === 0) return;
          
          // Only fetch for models that have predictions - avoids 404 storm for untrained models
          const modelsToFetch = availableModels.filter((m: any) => m.has_predictions === true);
          const promises = modelsToFetch.map((model: any) => fetchModelPredictions(model.id));
          await Promise.allSettled(promises);
        } finally {
          isFetchingPredictionsRef.current = false;
        }
      })();
      
      // Reset flags
      setForceRefetch(false);
      shouldFetchPredictions.current = false;
    }
  }, [databaseId, forceRefetch, filters, previsionData.modelPredictions, fetchModelPredictions, availableModels]);

  // Load available models on mount
  useEffect(() => {
    fetchAvailableModels();
  }, [fetchAvailableModels]);

  return {
    previsionData,
    availableModels,
    applyFilters,
    refetchModelData: fetchModelPredictions,
    refetchAvailableModels: fetchAvailableModels,
    filters,
  };
}; 